/**
 * SectionHeader
 *
 * Collapsible header for admin user sections. Displays title, count badge,
 * description, and a toggle.
 */
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const SectionHeader = ({ 
  sectionKey, 
  icon, 
  title, 
  count, 
  description, 
  color = "purple",
  isCollapsed,
  onToggle,
  selectedEnterpriseId,
  onClearEnterpriseFilter,
  enterprises = [],
  onSelectEnterprise
}) => {
  const ChevronIcon = isCollapsed ? ChevronDown : ChevronUp;
  const selectedEnterprise = selectedEnterpriseId
    ? enterprises.find(ent => ent.id === selectedEnterpriseId)
    : null;
  
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {icon}
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h2>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${color}-100 text-${color}-800 dark:bg-${color}-900/30 dark:text-${color}-400`}>
              {count} user{count !== 1 ? 's' : ''}
            </span>
            {selectedEnterprise && selectedEnterprise.enterpriseImage && (
              <span className="inline-flex items-center gap-2 ml-2">
                <img
                  src={selectedEnterprise.enterpriseImage}
                  alt={selectedEnterprise.enterpriseName || 'Enterprise'}
                  className="h-6 w-6 rounded object-contain bg-white"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedEnterprise.enterpriseName || 'Enterprise'}
                </span>
              </span>
            )}
            <button
              onClick={onToggle}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title={isCollapsed ? 'Expand section' : 'Collapse section'}
            >
              <ChevronIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {Array.isArray(enterprises) && enterprises.length > 0 && (
            <select
              value={selectedEnterpriseId || ''}
              onChange={(e) => onSelectEnterprise && onSelectEnterprise(e.target.value || null)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1"
              title="Filter by enterprise"
            >
              <option value="">All enterprises</option>
              {enterprises.map(ent => (
                <option key={ent.id} value={ent.id}>
                  {ent.enterpriseName || ent.enterpriseDomain}
                </option>
              ))}
            </select>
          )}
          {selectedEnterpriseId && onClearEnterpriseFilter && (
            <button
              onClick={onClearEnterpriseFilter}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Show All Users
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionHeader; 